"use client"

import { ReadonlyURLSearchParams } from "next/navigation"
import { trackFactFinderAddToCartAction, trackFactFinderProductClickAction } from "@/actions/factFinderTrackingActions"
import { sendGTMEvent } from "@next/third-parties/google"
import { ProductPriceContainerCtnrROV1 } from "@ub/pricing-client"

import { CardPrices, determineCardPrices } from "@/lib/price-utils"
import {
  buildGa4Item,
  removeEmptyTrackingValues,
  resetEcommerce,
  sendGTMEventIfAnalyticsAllowed,
  toCustomerType,
} from "@/lib/ga4TrackingHelper"
import { ReducedProductData } from "@/lib/storefront/product/product"
import {
  calculateCategoryPathString,
  determineEffectiveProductId,
  determineVariableAttributeValuesAsString,
} from "@/lib/storefront/product/productHelper"
import { logTracking, logTrackingFromServerResponse } from "@/lib/tracking/tracking-logger"
import { getPaginationSearchParams } from "@/components/search-and-category/pagination-info"

/*export function getSearchTermFromLocationOrSearchParams(searchParams: ReadonlyURLSearchParams) {
  const regex = /search\/([^\/]+)/
  const pathname = window.location.pathname
  const match = pathname.match(regex)
  if (match && match[1]) {
    return match[1]
  }
  return searchParams.get("searchTerm") ?? undefined
}*/

export function trackGTMAndFF(
  props: {
    trackingList?: string
    productData: ReducedProductData
    position: number
    page?: number
    pageSize?: number
    searchTerm?: string
    campaignId?: string
  }
  // searchParams: ReadonlyURLSearchParams
) {
  // Track in Google Tag Manager
  const trackedProductId = props.productData.id.toLowerCase().startsWith("e")
    ? props.productData.id.toUpperCase()
    : "A" + props.productData.id

  const productClickData = {
    event: "productClick",
    ecommerce: {
      click: {
        actionField: {
          list: props.trackingList ?? "",
          action: "click",
        },
        products: [
          {
            category: props.productData.category ?? "",
            id: trackedProductId,
            list: props.trackingList ?? "",
            name: props.productData.title,
            position: props.position,
            ffCampaignId: props.campaignId ?? "",
          },
        ],
      },
    },
  }
  sendGTMEvent(removeEmptyTrackingValues(productClickData))

  // Track in FactFinder using server action
  if (props.trackingList) {
    //const searchTermFromLocation = getSearchTermFromLocationOrSearchParams(searchParams)
    //const paginationSearchParams = getPaginationSearchParams(searchParams)

    trackFactFinderProductClickAction(
      props.productData,
      props.trackingList,
      props.position,
      props.searchTerm,
      props.page,
      props.pageSize,
      props.campaignId

      //searchTermFromLocation,
      //paginationSearchParams.page,
      //paginationSearchParams.pageSize
    ).then(logTrackingFromServerResponse, (error) =>
      console.error("Error calling trackFactFinderProductClickAction:", error)
    )
    logTracking("trackGTMAndFF", productClickData, "trackFactFinderProductClickAction")
  } else {
    logTracking("trackGTMAndFF", productClickData, "skipped, no trackingList")
  }
}

export function trackBannerClick(bannerCampaignId: string | undefined, bannerPosition: number | undefined) {
  const clickData = {
    event: "ffCampaignBannerClick",
    ffCampaign: {
      campaignId: bannerCampaignId ?? "",
      bannerPosition: bannerPosition ? bannerPosition.toString() : "",
    },
  }
  logTracking("trackBannerClick", clickData, null)
  sendGTMEvent(removeEmptyTrackingValues(clickData))
}

export function trackAddToBasket(
  isoLocale: string,
  isB2B: boolean,
  product: ReducedProductData | undefined,
  prices: ProductPriceContainerCtnrROV1 | undefined,
  quantity: number,
  options?: {
    isAnonymous?: boolean
    pagetyp?: string
    categoryPath?: string
  }
) {
  if (product && prices && quantity) {
    // GA4 requires gross prices per spec → compute with isB2B=false
    const cardPricesGross: CardPrices = determineCardPrices(isoLocale, false, prices, undefined, quantity, product.id)
    const categoryPath = options?.categoryPath ?? calculateCategoryPathString(product)
    const currency = product.currency || cardPricesGross.currency || "EUR"

    const customer_type = toCustomerType(options?.isAnonymous ?? false, isB2B)
    const pagetyp = options?.pagetyp ?? "pdp_artikel"

    const ga4Payload = {
      event: "add_to_cart",
      pagetyp,
      customer_type,
      ecommerce: {
        currency,
        value: Number((cardPricesGross.salePrice * quantity).toFixed(2)),
        items: [
          buildGa4Item({
            product,
            priceGross: cardPricesGross.salePrice,
            currency,
            quantity,
            variant: determineVariableAttributeValuesAsString(product),
            categoryPath,
          }),
        ],
      },
    }

    logTracking("trackAddToBasket", ga4Payload, "trackFactFinderAddToCartAction")
    sendGTMEventIfAnalyticsAllowed(resetEcommerce())
    sendGTMEventIfAnalyticsAllowed(ga4Payload)
    sendGTMEventIfAnalyticsAllowed(resetEcommerce())

    trackFactFinderAddToCartAction(
      product.id,
      quantity,
      cardPricesGross.salePrice,
      product.title,
      product.productMasterSKU
    ).then(logTrackingFromServerResponse, (error) =>
      console.error("Error calling trackFactFinderAddToCartAction:", error)
    )
  } else {
    logTracking("trackAddToBasket", "skipped, missing requirements", "skipped, missing requirements")
  }
}

export function trackRemoveFromBasket(
  isoLocale: string,
  isB2B: boolean,
  product: ReducedProductData | undefined,
  prices: ProductPriceContainerCtnrROV1 | undefined,
  quantity: number
) {
  if (product && prices && quantity) {
    const cardPricesGross: CardPrices = determineCardPrices(isoLocale, false, prices, undefined, quantity, product.id)
    const categoryPath = calculateCategoryPathString(product)
    const customer_type = toCustomerType(false, isB2B)
    const pagetyp = "cart"

    const ga4Payload = {
      event: "remove_from_cart",
      pagetyp,
      customer_type,
      ecommerce: {
        currency: product.currency,
        value: Number((cardPricesGross.salePrice * quantity).toFixed(2)),
        items: [
          buildGa4Item({
            product,
            priceGross: cardPricesGross.salePrice,
            currency: product.currency,
            quantity,
            variant: determineVariableAttributeValuesAsString(product),
            categoryPath,
          }),
        ],
      },
    }
    logTracking("trackRemoveFromBasket", ga4Payload, null)
    sendGTMEventIfAnalyticsAllowed(resetEcommerce())
    sendGTMEventIfAnalyticsAllowed(ga4Payload)
    sendGTMEventIfAnalyticsAllowed(resetEcommerce())
  }
}

export function trackFFProductClick(
  product: ReducedProductData | undefined,
  trackingList: string,
  position: number,
  searchTerm: string | undefined,
  campaignId?: string
) {
  if (product) {
    trackFactFinderProductClickAction(product, trackingList, position, searchTerm, undefined, undefined, campaignId).then((logTrackingEntries) => {
      logTrackingFromServerResponse(logTrackingEntries)
    })
  }
}

/**
 * Sends a GTM event for a guided‑selling impression.
 *
 * @param guided_selling_type - One of `popup`, `factfinder_banner`, `guided_selling_element` or `landingpage`.
 * @param guided_selling_campaign - Identifier for the campaign.
 * @param placement_page_type - Type of the page where the impression occurs.
 * @param placement_category - Category of the placement.
 * @param page_path - Current page path.
 */
export function trackGuidedSellingImpression(
  guided_selling_type: string,
  guided_selling_campaign: string,
  placement_page_type: string,
  placement_category: string,
  page_path: string
) {
  const guidedSellingData = {
    event: "guided_selling_impression",
    guided_selling: {
      guided_selling_type,
      guided_selling_campaign,
      placement_page_type,
      placement_category,
      page_path,
    },
  }
  logTracking("trackGuidedSellingImpression", guidedSellingData, null)

  sendGTMEvent(removeEmptyTrackingValues(guidedSellingData))
}

/**
 * Sends a GTM event for a guided‑selling click.
 *
 * @param guided_selling_type - Type of the guided selling (e.g., popup, factfinder_banner).
 * @param guided_selling_campaign - Campaign identifier.
 * @param cta_text - Text of the call‑to‑action button.
 * @param placement_page_type - Page type where the click occurred.
 * @param placement_category - Category of the placement.
 * @param page_path - Current page path.
 */
export function trackGuidedSellingClick(
  guided_selling_type: string,
  guided_selling_campaign: string,
  cta_text: string,
  placement_page_type: string,
  placement_category: string,
  page_path: string
) {
  const guidedSellingClickData = {
    event: "guided_selling_click",
    guided_selling: {
      guided_selling_type,
      guided_selling_campaign,
      cta_text,
      placement_page_type,
      placement_category,
      page_path,
    },
  }
  logTracking("trackGuidedSellingClick", guidedSellingClickData, null)

  sendGTMEvent(removeEmptyTrackingValues(guidedSellingClickData))
}

/**
 * Sends a GTM event for the start of a guided‑selling session.
 *
 * @param guided_selling_campaign - Identifier for the campaign.
 * @param entry_type - Entry point type (`popup`, `banner`, or `category_element`).
 * @param page_path - Current page path.
 */
export function trackGuidedSellingStart(guided_selling_campaign: string, entry_type: string, page_path: string) {
  const guidedSellingStartData = {
    event: "guided_selling_start",
    guided_selling: {
      guided_selling_campaign,
      entry_type,
      page_path,
    },
  }
  logTracking("trackGuidedSellingStart", guidedSellingStartData, null)

  sendGTMEvent(removeEmptyTrackingValues(guidedSellingStartData))
}

/**
 * Sends a GTM event for the result of a guided‑selling session.
 *
 * @param guided_selling_campaign - Identifier for the campaign.
 * @param result_type - Type of result (`product_list` or `single_product`).
 * @param result_count - Number of recommended products returned.
 */
export function trackGuidedSellingResult(guided_selling_campaign: string, result_type: string, result_count: number) {
  const guidedSellingResultData = {
    event: "guided_selling_result",
    guided_selling: {
      guided_selling_campaign,
      result_type,
      result_count,
    },
  }
  logTracking("trackGuidedSellingResult", guidedSellingResultData, null)

  sendGTMEvent(removeEmptyTrackingValues(guidedSellingResultData))
}
